import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import api from '../components/api';

import StepIndicator   from './register/StepIndicator';
import StepCompte      from './register/StepCompte';
import StepProfil      from './register/StepProfil';
import StepDiplomes    from './register/StepDiplomes';
import StepLangues     from './register/StepLangues';
import StepExperiences from './register/StepExperiences';

const STEP_META = [
    { title: 'Créer votre compte',            subtitle: 'Informations de connexion' },
    { title: 'Votre profil',                  subtitle: 'Informations personnelles' },
    { title: 'Vos diplômes',                  subtitle: 'Au moins un diplôme requis' },
    { title: 'Vos langues',                   subtitle: 'Au moins une langue requise' },
    { title: 'Expériences professionnelles',  subtitle: 'Facultatif — peut être complété plus tard' },
];

const RegisterPage = () => {
    const navigate = useNavigate();
    const { login } = useGlobale();

    const [step,    setStep]    = useState(0);
    const [loading, setLoading] = useState(false);

    const [typesDiplome,      setTypesDiplome]      = useState([]);
    const [niveauxEtude,      setNiveauxEtude]      = useState([]);
    const [domainesFormation, setDomainesFormation] = useState([]);
    const [listeLangues,      setListeLangues]      = useState([]);
    const [niveauxLangue,     setNiveauxLangue]     = useState([]);

    const [compte, setCompte] = useState({
        first_name: '', last_name: '', email: '', telephone: '', password: '', confirm_password: ''
    });
    const [profil, setProfil] = useState({
        genre: '', date_naissance: '', lieu_naissance: '', nationalite: 'NE',
        situation_familiale: '', telephone2: '', adresse: '', lien_linkdin: '',
        titre: '', apropos: ''
    });
    const [diplomes,    setDiplomes]    = useState([]);
    const [langues,     setLangues]     = useState([]);
    const [experiences, setExperiences] = useState([]);

    useEffect(() => {
        api.get('demandeur/register-referentiels/')
            .then(r => {
                setTypesDiplome(r.data.types_diplome);
                setNiveauxEtude(r.data.niveaux_etude);
                setDomainesFormation(r.data.domaines_formation);
                setListeLangues(r.data.liste_langues);
                setNiveauxLangue(r.data.niveaux_langue);
            })
            .catch(() => {});
    }, []);

    const handleFinalize = async (includeExperiences) => {
        setLoading(true);
        try {
            const payload = {
                first_name:       compte.first_name,
                last_name:        compte.last_name,
                email:            compte.email,
                telephone:        compte.telephone || null,
                password:         compte.password,
                confirm_password: compte.confirm_password,
                profil: {
                    genre:               profil.genre,
                    date_naissance:      profil.date_naissance,
                    lieu_naissance:      profil.lieu_naissance,
                    nationalite:         profil.nationalite,
                    situation_familiale: profil.situation_familiale || null,
                    telephone2:          profil.telephone2 || null,
                    adresse:             profil.adresse,
                    lien_linkdin:        profil.lien_linkdin || null,
                    titre:               profil.titre || null,
                    apropos:             profil.apropos || null,
                },
                diplomes:    diplomes.map(({ _typeName, ...d }) => ({
                    ...d,
                    diplome: d.diplome === 'autre' ? null : d.diplome || null,
                })),
                langues:     langues.map(({ _nom, ...l }) => l),
                experiences: includeExperiences ? experiences : [],
            };

            await api.post('utilisateur/register/', payload);

            const loginRes = await api.post('utilisateur/signin/', {
                username: compte.email,
                password: compte.password,
            });
            await login(loginRes.data.tokens, loginRes.data.user);

            toast.success("Inscription réussie ! Bienvenue sur ANPE.");
            navigate('/');
        } catch (err) {
            const data = err.response?.data;
            if (data) {
                const msg = Object.entries(data)
                    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : (typeof v === 'object' ? JSON.stringify(v) : v)}`)
                    .join(' | ');
                toast.error(msg, { autoClose: 8000 });
            } else {
                toast.error("Erreur lors de l'inscription.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white py-6 px-4 flex items-start justify-center">
            <div className="w-full max-w-xl">

                <div className="text-center mb-5">
                    <img className="mx-auto h-12 w-auto" src="/logo-anpe.png" alt="ANPE" />
                    <p className="mt-2 text-xs text-gray-500">
                        Déjà inscrit ?{' '}
                        <Link to="/connexion" className="font-medium text-orange-600 hover:text-orange-500">Se connecter</Link>
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <StepIndicator current={step} />

                    <div className="mb-5">
                        <h2 className="text-lg font-bold text-gray-900">{STEP_META[step].title}</h2>
                        <p className="text-xs text-gray-500 mt-0.5">{STEP_META[step].subtitle}</p>
                    </div>

                    {step === 0 && (
                        <StepCompte data={compte} onChange={setCompte} onNext={() => setStep(1)} />
                    )}
                    {step === 1 && (
                        <StepProfil data={profil} onChange={setProfil} onNext={() => setStep(2)} onBack={() => setStep(0)} />
                    )}
                    {step === 2 && (
                        <StepDiplomes
                            diplomes={diplomes} onDiplomesChange={setDiplomes}
                            typesDiplome={typesDiplome} niveauxEtude={niveauxEtude}
                            domainesFormation={domainesFormation}
                            onNext={() => setStep(3)} onBack={() => setStep(1)}
                        />
                    )}
                    {step === 3 && (
                        <StepLangues
                            langues={langues} onLanguesChange={setLangues}
                            listeLangues={listeLangues}
                            onNext={() => setStep(4)} onBack={() => setStep(2)}
                        />
                    )}
                    {step === 4 && (
                        <StepExperiences
                            experiences={experiences} onExperiencesChange={setExperiences}
                            onFinalize={handleFinalize} onBack={() => setStep(3)}
                            loading={loading}
                        />
                    )}
                </div>

            </div>
        </div>
    );
};

export default RegisterPage;
